'use client';
import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort } from '@/lib/financial-model';

function CustomTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { amount: number } }>;
}) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0];
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--divider)',
        borderRadius: '4px',
        padding: '10px 14px',
      }}
    >
      <p style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-primary)' }}>{d.name}</p>
      <p
        style={{
          fontSize: '0.75rem',
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-primary)',
          marginTop: 4,
        }}
      >
        {fmtCrShort(d.payload.amount)}
      </p>
      <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)' }}>
        {d.value.toFixed(1)}% of total
      </p>
    </div>
  );
}

export default function FundingPieChart() {
  const { model } = useModel();
  const equity = model.assumptions.equityAmount;
  const debt = model.assumptions.debtAmount;
  const total = equity + debt;

  const data = [
    {
      name: 'Partner Equity',
      value: parseFloat(((equity / total) * 100).toFixed(1)),
      amount: equity,
      color: 'var(--gold)',
    },
    {
      name: 'Debt (CGTMSE)',
      value: parseFloat(((debt / total) * 100).toFixed(1)),
      amount: debt,
      color: 'var(--accent)',
    },
  ];

  return (
    <div>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={48}
            outerRadius={72}
            paddingAngle={3}
            dataKey="value"
          >
            {data.map((entry, idx) => (
              <Cell key={`funding-cell-${idx}`} fill={entry.color} strokeWidth={0} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              fontSize: '0.6rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
            iconType="circle"
            iconSize={7}
            formatter={(v) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {data.map((d) => (
          <div
            key={`funding-legend-${d.name}`}
            className="text-center p-2 rounded"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--divider)' }}
          >
            <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginBottom: 2 }}>
              {d.name}
            </p>
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.8rem',
                fontWeight: 700,
                color: d.color,
              }}
            >
              {fmtCrShort(d.amount)}
            </p>
            <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)' }}>
              {d.value.toFixed(1)}%
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
